import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Text, Button, IconButton, Input, InputGroup, InputLeftElement, Image, Skeleton, Switch, useDisclosure, useToast } from '@chakra-ui/react';
import { Search, Add } from '@mui/icons-material';

import AdminLayout from '../components/admin/AdminLayout';
import { Segmented } from '../components/admin/ui';
import ProductEditor from '../components/admin/ProductEditor';
import { getAllProducts, updateProduct } from '../services/ProductServices';
import { getAllCategories } from '../services/CategoryServices';
import { getAllGenres } from '../services/GenreServices';
import { inr } from '../utils/format';

// Product / Category / Price / In store
const COLUMNS = 'minmax(0, 3fr) minmax(0, 1.4fr) 110px 90px';

const ProductsforAdmin = () => {

  const toast = useToast();
  const { state } = useLocation();
  const editor = useDisclosure();
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    getAllProducts().then(r => setProducts(r.allProducts || [])).catch(() => setProducts([]));
    getAllCategories().then(r => setCategories(r.allCategories || []));
    getAllGenres().then(r => setGenres(r.allGenres || []));
  }, []);

  // Dashboard's "+ Product" button lands here with { create: true }
  useEffect(() => {
    if (state?.create) { setEditing(null); editor.onOpen(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const categoryName = (id) => categories.find(c => c._id === id)?.name || 'No category';

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...(products || [])]
      .reverse()
      .filter(p => (category === 'all' || p.category === category) && (!q || p.name.toLowerCase().includes(q) || p.color?.includes(q)));
  }, [products, query, category]);

  const hiddenCount = (products || []).filter(p => !p.status).length;

  const openEditor = (product) => { setEditing(product); editor.onOpen(); };

  const toggleStatus = async (product) => {
    const status = !product.status;
    setProducts(prev => prev.map(p => p._id === product._id ? { ...p, status } : p));
    try {
      await updateProduct(product._id, { status });
      toast({ title: status ? `${product.name} is live` : `${product.name} hidden from store`, status: 'success', duration: 1500, position: 'top' });
    } catch (e) {
      setProducts(prev => prev.map(p => p._id === product._id ? { ...p, status: !status } : p));
      toast({ title: 'Could not update', status: 'error', duration: 2500 });
    }
  };

  return (
    <AdminLayout
      title='Products'
      subtitle={products ? `${products.length} products${hiddenCount ? ` · ${hiddenCount} hidden` : ''}` : 'Loading…'}
      actions={<Button display={{ base: 'none', md: 'inline-flex' }} variant='ink' height='40px' px='18px' fontSize='15px' leftIcon={<Add fontSize='small' />} onClick={() => openEditor(null)} >Add product</Button>}
    >
      <InputGroup mb='12px' >
        <InputLeftElement pointerEvents='none' height='44px' color='blackAlpha.600' ><Search fontSize='small' /></InputLeftElement>
        <Input bg='raano.white' height='44px' borderRadius='6px' borderColor='#D9D9D9' _hover={{ borderColor: 'blackAlpha.500' }} _focusVisible={{ borderColor: 'raano.black', boxShadow: 'none' }} type='search' placeholder='Search products' value={query} onChange={(e) => setQuery(e.target.value)} />
      </InputGroup>

      <Box mb='16px' >
        <Segmented label='Category' value={category} onChange={setCategory} options={[{ value: 'all', label: 'All', count: (products || []).length }, ...categories.map(c => ({ value: c._id, label: c.name, count: (products || []).filter(p => p.category === c._id).length }))]} />
      </Box>

      <Box bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' overflow='hidden' >
        {/* Column headings (desktop) */}
        <Box display={{ base: 'none', md: 'grid' }} gridTemplateColumns={COLUMNS} gap='16px' px='16px' py='10px' bg='#FAFAFA' borderBottom='1px solid' borderColor='raano.border' fontSize='12px' fontWeight={600} color='raano.muted' textTransform='uppercase' letterSpacing='0.04em' >
          <Text>Product</Text><Text>Category</Text><Text textAlign='right' >Price</Text><Text textAlign='center' >In store</Text>
        </Box>
        {products === null
          ? Array(5).fill(null).map((_, i) => <Skeleton key={i} height='64px' m='12px' borderRadius='8px' />)
          : visible.length === 0
            ? <Text textAlign='center' py='40px' color='raano.muted' >No products match.</Text>
            : visible.map((p, i) => (
              <Box key={p._id} display={{ base: 'flex', md: 'grid' }} gridTemplateColumns={COLUMNS} alignItems='center' gap={{ base: '12px', md: '16px' }} px='16px' py='10px'
                borderTop={i ? '1px solid' : 'none'} borderColor='raano.border' transition='background .125s' _hover={{ bg: '#FAFAFA' }} >
                <Box as='button' display='flex' alignItems='center' gap='12px' flex={1} minWidth={0} textAlign='left' onClick={() => openEditor(p)} opacity={p.status ? 1 : 0.55} >
                  <Image src={p.imageUrl} alt='' width='44px' height='58px' objectFit='cover' objectPosition='top' borderRadius='6px' bg='raano.imageBg' flexShrink={0} fallback={<Box width='44px' height='58px' borderRadius='6px' bg='raano.imageBg' />} />
                  <Box minWidth={0} >
                    <Text fontSize='14px' fontWeight={600} noOfLines={1} _groupHover={{ textDecoration: 'underline' }} >{p.name}</Text>
                    <Text fontSize='12px' color='raano.muted' noOfLines={1} textTransform='capitalize' >{p.color} · {(p.sizes || []).join(', ')}</Text>
                    <Text display={{ base: 'block', md: 'none' }} fontSize='12px' color='raano.muted' isTruncated >{categoryName(p.category)}</Text>
                    <Text display={{ base: 'block', md: 'none' }} fontSize='16px' fontWeight={700} sx={{ fontStretch: '75%' }} mt='2px' >{inr(p.price)}</Text>
                  </Box>
                </Box>
                <Text display={{ base: 'none', md: 'block' }} fontSize='14px' isTruncated >{categoryName(p.category)}</Text>
                <Text display={{ base: 'none', md: 'block' }} fontSize='17px' fontWeight={700} textAlign='right' sx={{ fontStretch: '75%' }} >{inr(p.price)}</Text>
                <Box textAlign='center' flexShrink={0} >
                  <Switch isChecked={p.status} onChange={() => toggleStatus(p)} aria-label={`Show ${p.name} in store`} />
                  <Text fontSize='11px' color='raano.muted' mt='2px' >{p.status ? 'Live' : 'Hidden'}</Text>
                </Box>
              </Box>
            ))}
      </Box>

      {/* Mobile: floating add button above the admin tab bar */}
      <IconButton
        display={{ base: 'inline-flex', md: 'none' }}
        position='fixed'
        right={4}
        bottom='calc(80px + env(safe-area-inset-bottom))'
        zIndex={440}
        width='56px'
        height='56px'
        borderRadius='full'
        bg='raano.black'
        color='raano.white'
        _hover={{ bg: '#1A1A1A' }}
        boxShadow='aiPill'
        aria-label='Add product'
        icon={<Add />}
        onClick={() => openEditor(null)}
      />

      <ProductEditor
        isOpen={editor.isOpen}
        onClose={editor.onClose}
        product={editing}
        genres={genres}
        categories={categories}
        onSaved={(saved) => setProducts(prev => prev.some(p => p._id === saved._id) ? prev.map(p => p._id === saved._id ? saved : p) : [...prev, saved])}
        onDeleted={(id) => setProducts(prev => prev.filter(p => p._id !== id))}
      />
    </AdminLayout>
  )
}

export default ProductsforAdmin;
